import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";

import { Autoplay, Pagination } from "swiper/modules";
import styled, { keyframes } from "styled-components";
import colors from "theme/colors";
import { Calendar, ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import { Column } from "styled/alignment/Column";
import eventTypes from "theme/eventTypes";
import breakpoints from "theme/breakpoints";
import Text from "components/_UI/Text";
import { Row } from "styled/alignment/Row";
import { FullyCentered } from "styled/alignment/Center";
import Icon from "components/_UI/Icon";
import { useEventDetailModal } from "context/events/EventDetailModal";
import date from 'helpers/date';
import useFavorite from 'hooks/useFavorite';

const CarouselFavoriteButton = ({ slug, initialFavorited }) => {
    const { isFavorited, toggleFavorite } = useFavorite(slug, initialFavorited);
    return (
        <FavoriteContent onClick={(e) => {
            e.stopPropagation();
            toggleFavorite();
        }}>
            <Heart
                size={14}
                color={isFavorited ? colors.occasionalPink : colors.nightLight}
                fill={isFavorited ? colors.occasionalPink : 'none'}
            />
        </FavoriteContent>
    );
};

const EventCarousel = ({ data }) => {
  const { setEventDetailModal } = useEventDetailModal();
  const swiperRef = useRef(null);

  const handleNext = () => swiperRef.current?.slideNext();
  const handlePrev = () => swiperRef.current?.slidePrev();

  const handleSlideClick = (item, index) => {
    if (swiperRef.current?.realIndex === index) {
      setEventDetailModal(item);
      return;
    }
    swiperRef.current?.slideToLoop(index, 500);
  };

  return (
    <CarouselWrapper>
      <ArrowLeft onClick={handlePrev}>
        <ChevronLeft size={20} color={colors.night} />
      </ArrowLeft>
      <ArrowRight onClick={handleNext}>
        <ChevronRight size={20} color={colors.night} />
      </ArrowRight>

      <CarouselContainer>
        <StyledSwiper
          slidesPerView={3}
          spaceBetween={12}
          centeredSlides={true}
          loop={data.length >= 6}
          breakpoints={{
              0: {
                  slidesPerView: 1,
                  spaceBetween: 0,
                  centeredSlides: false,
              },
              1200: {
                  slidesPerView: 3,
                  spaceBetween: 12,
                  centeredSlides: true,
              },
          }}
          autoplay={{
            delay: 4000,
            disableOnInteraction: false,
          }}
          pagination={{
            clickable: true,
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          modules={[Autoplay, Pagination]}
        >
          {data.map((item, index) => (
            <SwiperSlide key={item?.id} onClick={() => handleSlideClick(item, index)}>
              <Content>
                <ImageWrapper>
                  <TagsContainer>
                    {(() => {
                      const isFree = item?.classification?.name === eventTypes.free.slug;
                      return (
                        <Tag isType={isFree ? "free" : "paid"}>
                          <Text name="mini" color={colors.neutralLight} paddingBottom={0}>
                            {isFree ? "Gratuito" : "Pago"}
                          </Text>
                        </Tag>
                      );
                    })()}

                    {item?.trending && (
                      <Tag isTrend>
                        <Column width='16px' height='16px'>
                          <Icon name="fire-filled" color={colors.occasionalPink} />
                        </Column>
                        <Text
                          name="mini"
                          color={colors.occasionalPink}
                          paddingBottom={0}
                          marginLeft={"4px"}
                        >
                          Em alta
                        </Text>
                      </Tag>
                    )}
                  </TagsContainer>

                  <CarouselFavoriteButton slug={item?.slug} initialFavorited={item?.is_favorited} />

                  <SlideImage
                    src={item?.images?.length ? item.images[0] : '/assets/images/placeholder-events.png'}
                    alt={item?.name}
                    onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
                  />

                  <BottomOverlay type={item?.classification?.name === eventTypes.free.slug ? 'free' : 'paid'}>
                    <Text color={colors.neutralLight} type='bold' ellipsisAt='90%' name='small' paddingBottom={0}>
                      {item?.name}
                    </Text>
                    <Row marginTop='6px' verticalCenter>
                      <Calendar size={14} color={colors.neutralLight} />
                      <Text name='mini' color={colors.neutralLight} marginLeft='4px' paddingBottom={0}>
                        {item?.dates?.[0]?.date ? date.format(item.dates[0].date) : 'Data a definir'} · {item?.dates?.[0]?.start ? date.formatTime(item.dates[0].start) : '--:--'}
                      </Text>
                    </Row>
                  </BottomOverlay>
                </ImageWrapper>
              </Content>
            </SwiperSlide>
          ))}
        </StyledSwiper>
      </CarouselContainer>

    </CarouselWrapper>
  );
};


const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CarouselWrapper = styled.div`
  position: relative;
  margin-top: -14px;
  animation: ${fadeInUp} 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
`;

const CarouselContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
`;

const StyledSwiper = styled(Swiper)`
  width: 100%;
  height: auto;
  padding-top: 8px;
  padding-bottom: 4px;

  .swiper-slide {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    transition: transform 0.35s ease, opacity 0.35s ease, filter 0.35s ease;
    cursor: grab;
    transform: scale(0.84);
    opacity: 0.55;
    filter: brightness(0.88);
  }

  .swiper-slide-active {
    transform: scale(1.06);
    opacity: 1;
    filter: brightness(1);
    z-index: 3;
    cursor: pointer;
  }

  .swiper-slide-next,
  .swiper-slide-prev {
    transform: scale(0.93);
    opacity: 0.82;
    filter: brightness(0.94);
    z-index: 2;
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    .swiper-slide {
      transform: none;
      opacity: 1;
      filter: none;
    }
    .swiper-slide-active,
    .swiper-slide-next,
    .swiper-slide-prev {
      transform: none;
      opacity: 1;
      filter: none;
    }
  }

  .swiper-pagination {
    position: relative;
    margin-top: 20px;
    text-align: center;
  }

  .swiper-pagination-bullet {
    background: ${colors.nightLight};
    opacity: 0.6;
    width: 8px;
    height: 8px;
    margin: 0 5px;
    border-radius: 50%;
    transition: opacity 0.3s;
    cursor: pointer;
  }

  .swiper-pagination-bullet-active {
    background: ${colors.occasionalPurple};
    opacity: 1;
  }
`;

const ArrowLeft = styled(FullyCentered)`
  position: absolute;
  left: 18px;
  top: 40%;
  transform: translateY(-50%);
  transition: all ease-in-out .2s;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.night}22;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    border-color: ${colors.occasionalPurple};
    svg { stroke: ${colors.occasionalPurple}; }
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    display: none;
  }
`;

const ArrowRight = styled(FullyCentered)`
  position: absolute;
  right: 22px;
  top: 40%;
  transform: translateY(-50%);
  transition: all ease-in-out .2s;
  background: ${colors.neutralLight};
  border: 1px solid ${colors.night}22;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
  z-index: 4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    border-color: ${colors.occasionalPurple};
    svg { stroke: ${colors.occasionalPurple}; }
  }

  @media screen and (max-width: calc(${breakpoints.lg} - 1px)) {
    display: none;
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
`;

const SlideImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const Content = styled(Column)`
  width: 100%;
`;

const TagsContainer = styled(Row)`
  position: absolute;
  top: 10px;
  left: 8px;
  width: auto;
  z-index: 2;
`;

const Tag = styled(FullyCentered)`
  padding: 4px 8px;
  border-radius: 6px;
  margin-right: 4px;
  flex-direction: row;
  width: auto;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  background: ${({ isType, isTrend }) =>
    isTrend ? 'rgba(255, 255, 255, 0.92)' :
    isType === 'free' ? 'rgba(43, 200, 102, 0.85)' :
    isType === 'paid' ? 'rgba(159, 22, 219, 0.85)' :
    'rgba(255, 255, 255, 0.92)'
  };
  border: none;

  ${({ isTrend }) => isTrend && `
    border: 1px solid ${colors.occasionalPink}40;
  `}
`;

const FavoriteContent = styled(FullyCentered)`
  position: absolute;
  right: 10px;
  top: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  z-index: 2;
`;

const BottomOverlay = styled(Column)`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 10px 12px;
  background: ${({ type }) => type === 'free' ? 'rgba(43, 200, 102, 0.75)' : 'rgba(159, 22, 219, 0.75)'};
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 0 0 12px 12px;
  z-index: 2;
`;

export default EventCarousel;
